import React, { useCallback, useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    CounterpartyReviewField,
    GENERIC_COUNTERPARTY_REVIEW_FIELDS,
    GenericCounterpartyReviewField,
} from "@common/types/counterpartyFieldReview";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import { BaseFilterProps } from "../../lib";
import { ModeRadioGroup } from "../ReviewFilter/ModeRadioGroup";
import {
    ReviewerFilterSection,
    useReverseReviewers,
} from "../ReviewFilter/ReviewerFilterSection";
import styles from "../ReviewFilter/ReviewFilter.module.scss";

const REVIEW_FIELD_OPTIONS: FilterMultiselectOption[] = [
    { value: CounterpartyReviewField.BASIC_INFO, label: "Basic info" },
    { value: CounterpartyReviewField.ADDRESS, label: "Address" },
    { value: CounterpartyReviewField.ELIGIBLE_1099, label: "1099 eligible" },
    {
        value: CounterpartyReviewField.TAX_CLASSIFICATION,
        label: "Tax classification",
    },
];

const FIELD_MODE_OPTIONS: Array<{
    value: "any" | "every";
    label: string;
}> = [
    { value: "any", label: "Any field" },
    { value: "every", label: "Every field" },
];

const REVIEW_FIELD_VALUES = new Set<string>(GENERIC_COUNTERPARTY_REVIEW_FIELDS);

const isCounterpartyReviewField = (
    value: FilterMultiselectValue,
): value is GenericCounterpartyReviewField =>
    typeof value === "string" && REVIEW_FIELD_VALUES.has(value);

export const CounterpartyReviewFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const reviewedFields = useMemo(
        () => filters.counterpartyReviewedFields ?? [],
        [filters.counterpartyReviewedFields],
    );
    const unreviewedFields = useMemo(
        () => filters.counterpartyUnreviewedFields ?? [],
        [filters.counterpartyUnreviewedFields],
    );
    const reviewerIds = useMemo(
        () => filters.counterpartyReviewerIds ?? [],
        [filters.counterpartyReviewerIds],
    );
    const reviewerMode = filters.counterpartyReviewerMode ?? "any";
    const reviewedFieldsMode = filters.counterpartyReviewedFieldsMode ?? "any";
    const unreviewedFieldsMode =
        filters.counterpartyUnreviewedFieldsMode ?? "every";

    const isFilterActive = useMemo(
        () =>
            reviewedFields.length > 0 ||
            unreviewedFields.length > 0 ||
            reviewerIds.length > 0 ||
            filters.counterpartyReviewedFieldsMode !== undefined ||
            filters.counterpartyUnreviewedFieldsMode !== undefined ||
            filters.counterpartyReviewerMode !== undefined,
        [
            reviewedFields,
            unreviewedFields,
            reviewerIds,
            filters.counterpartyReviewedFieldsMode,
            filters.counterpartyUnreviewedFieldsMode,
            filters.counterpartyReviewerMode,
        ],
    );

    const handleReset = useCallback(() => {
        onChange({
            counterpartyReviewedFields: undefined,
            counterpartyReviewedFieldsMode: undefined,
            counterpartyUnreviewedFields: undefined,
            counterpartyUnreviewedFieldsMode: undefined,
            counterpartyReviewerIds: undefined,
            counterpartyReviewerMode: undefined,
        });
    }, [onChange]);

    const handleReviewerIdsChange = useCallback(
        (ids: number[] | undefined) =>
            onChange({ counterpartyReviewerIds: ids }),
        [onChange],
    );

    const reverseReviewers = useReverseReviewers({
        reviewerIds,
        onReviewerIdsChange: handleReviewerIdsChange,
    });

    return (
        <div className={styles.filter}>
            <div className={styles.sectionTitle}>Reviewed</div>
            <ModeRadioGroup
                name="counterpartyReviewedFieldsMode"
                value={reviewedFieldsMode}
                options={FIELD_MODE_OPTIONS}
                onChange={(value) =>
                    onChange({ counterpartyReviewedFieldsMode: value })
                }
            />

            <div className={styles.sectionWithTopBorder}>
                <FilterMultiselect
                    inDropdown={false}
                    searchable={false}
                    options={REVIEW_FIELD_OPTIONS}
                    value={reviewedFields}
                    onChange={(val: FilterMultiselectValue[]) => {
                        const fields = val.filter(isCounterpartyReviewField);
                        onChange({
                            counterpartyReviewedFields:
                                fields.length > 0 ? fields : undefined,
                            counterpartyReviewedFieldsMode:
                                fields.length === 0
                                    ? undefined
                                    : reviewedFieldsMode,
                        });
                    }}
                    name="counterpartyReviewedFields"
                    onBlur={noop}
                />
            </div>

            <div className={styles.sectionWithTopBorder}>
                <div className={styles.sectionTitle}>Unreviewed</div>

                <ModeRadioGroup
                    name="counterpartyUnreviewedFieldsMode"
                    value={unreviewedFieldsMode}
                    options={FIELD_MODE_OPTIONS}
                    onChange={(value) =>
                        onChange({ counterpartyUnreviewedFieldsMode: value })
                    }
                />
            </div>
            <div className={styles.sectionWithTopBorder}>
                <FilterMultiselect
                    inDropdown={false}
                    options={REVIEW_FIELD_OPTIONS}
                    value={unreviewedFields}
                    searchable={false}
                    onChange={(val: FilterMultiselectValue[]) => {
                        const fields = val.filter(isCounterpartyReviewField);
                        onChange({
                            counterpartyUnreviewedFields:
                                fields.length > 0 ? fields : undefined,
                            counterpartyUnreviewedFieldsMode:
                                fields.length === 0
                                    ? undefined
                                    : unreviewedFieldsMode,
                        });
                    }}
                    name="counterpartyUnreviewedFields"
                    onBlur={noop}
                />
            </div>

            <ReviewerFilterSection
                modeName="counterpartyReviewerMode"
                idsName="counterpartyReviewerIds"
                reviewerIds={reviewerIds}
                reviewerMode={reviewerMode}
                onModeChange={(mode) =>
                    onChange({ counterpartyReviewerMode: mode })
                }
                onReviewerIdsChange={handleReviewerIdsChange}
            />
            {isFilterActive && (
                <FilterActionsFooter
                    deselectFunction={handleReset}
                    selectedCount={
                        reviewedFields.length +
                        unreviewedFields.length +
                        reviewerIds.length
                    }
                    reverseFunction={reverseReviewers}
                />
            )}
        </div>
    );
};
